import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Award, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { missionVision, coreValues, director, trainingInstitute } from '../data/mock';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-maritime-primary">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1618317497044-cf78ada06f92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxoYXJib3J8ZW58MHx8fHwxNzcxMjAyMTk1fDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-white mb-6">About Gamma Marine</h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Professional marine inspection and surveying company dedicated to delivering 
              accurate, independent, and reliable services to the global maritime industry.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-maritime-primary mb-6">
                Leading Marine Survey Professionals
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Gamma Marine Limited is a professional marine inspection and surveying company 
                  based in Tauranga, New Zealand. We are dedicated to delivering accurate, 
                  independent, and reliable marine survey services to the global maritime industry.
                </p>
                <p>
                  As an <strong>approved National Inspection Agency by Maritime New Zealand</strong>, 
                  we uphold the highest standards of compliance, safety, and service excellence. 
                  We proudly provide our professional services across Australia, New Zealand, 
                  and the Pacific Islands.
                </p>
                <p>
                  Our team consists of experienced, certified marine surveyors who bring extensive 
                  industry knowledge and expertise to every project. We are committed to quality, 
                  precision, and maintaining the integrity that our clients depend on.
                </p>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1759272548449-7b689a81c8fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHx0ZWFtJTIwaGFyYm9yfGVufDB8fHx8MTc3MTIwMjE5MHww&ixlib=rb-4.1.0&q=85"
                alt="Marine Professionals"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-b from-maritime-primary/5 to-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <Card className="border-2 border-maritime-accent/20 hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-maritime-accent/10 rounded-xl flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-maritime-accent" />
                </div>
                <h3 className="text-2xl font-bold text-maritime-primary mb-4">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {missionVision.mission}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-maritime-accent/20 hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-maritime-accent/10 rounded-xl flex items-center justify-center mb-6">
                  <Eye className="w-8 h-8 text-maritime-accent" />
                </div>
                <h3 className="text-2xl font-bold text-maritime-primary mb-4">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {missionVision.vision}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Core Values */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-maritime-primary mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our values guide every decision we make and every service we provide.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {coreValues.map((value) => {
              const IconComponent = require('lucide-react')[value.icon];
              return (
                <Card 
                  key={value.id} 
                  className="text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-maritime-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-7 h-7 text-maritime-accent" />
                    </div>
                    <h4 className="font-semibold text-maritime-primary mb-2">{value.title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Director Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-maritime-accent/20 overflow-hidden">
              <CardContent className="p-0">
                <div className="grid md:grid-cols-3 gap-0">
                  <div className="bg-maritime-primary p-8 flex flex-col justify-center items-center text-center">
                    <div className="w-24 h-24 bg-maritime-accent/20 rounded-full flex items-center justify-center mb-4">
                      <Award className="w-12 h-12 text-maritime-accent" />
                    </div>
                    <h4 className="text-white font-semibold text-lg">{director.role}</h4>
                  </div>
                  <div className="md:col-span-2 p-8 flex flex-col justify-center">
                    <h3 className="text-2xl font-bold text-maritime-primary mb-2">
                      {director.name}
                    </h3>
                    <p className="text-maritime-accent font-medium mb-2">{director.title}</p>
                    <a 
                      href={`mailto:${director.email}`}
                      className="text-maritime-secondary hover:text-maritime-accent transition-colors mb-4 inline-flex items-center gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      {director.email}
                    </a>
                    <p className="text-gray-600 leading-relaxed">
                      Leading Gamma Marine with extensive maritime expertise and a commitment 
                      to excellence in marine surveying. Bringing years of industry experience 
                      and professional qualifications to ensure the highest standards of service.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Training Institute */}
      <section className="py-20 bg-maritime-primary text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1595587637401-83ff822bd63e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxoYXJib3J8ZW58MHx8fHwxNzcxMjAyMTk1fDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-maritime-accent/20 backdrop-blur-sm border border-maritime-accent/30 rounded-full px-4 py-2 mb-6">
              <Award className="w-4 h-4 text-maritime-accent" />
              <span className="text-sm font-medium">Education & Training</span>
            </div>
            <h2 className="text-4xl font-bold mb-6">{trainingInstitute.title}</h2>
            <p className="text-xl text-gray-200 mb-4 leading-relaxed">
              {trainingInstitute.description}
            </p>
            <p className="text-lg text-maritime-accent font-medium mb-8">
              In Partnership with {trainingInstitute.partner}
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <p className="text-gray-200 leading-relaxed">
                We are committed to developing the next generation of maritime professionals 
                through internationally recognized education and training programs. Our 
                collaboration ensures industry-leading standards and comprehensive skill development.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-maritime-accent/10 to-maritime-primary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-maritime-primary mb-4">
              Partner With Industry Experts
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Experience the difference of working with an approved national inspection agency.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-maritime-accent hover:bg-maritime-accent/90 text-white"
              >
                Contact Our Team
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
